import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import NavigateMenuButton from './NavigateMenuButton';

interface DealerInfo {
  dealerId: number;
  dealerName: string;
  dealerEmail: string;
  uen: string;
  address: string;
  officeNumber: string;
  faxNumber: string;
  website: string;
  contactPersonName: string;
  contactNumber: string;
  imageUrl: string;
  dealerCompanyId: number;
  dealerCompanyName: string;
  dealerTypeId: number;
  dealerTypeName: string;
}

interface SalesPerson {
  name: string;
  email: string;
  contactNo: string;
}

interface RegisterInfo {
  coeRemainingYears: number;
  coeRemainingMonths: number;
  coerebateAmount: number;
  vehicleModel?: string;
}

interface SalesInfo {
  askingPrice: number;
}

interface Vehicle {
  vehicleId: number;
  vehicleDealerInfo: DealerInfo;
  vehicleSalesInfo: SalesInfo;
  vehicleRegisterInfo: RegisterInfo;
  salesPersons: SalesPerson[];
}

interface DealerCardProps {
  vehicle: Vehicle;
  handleOpen?: () => void;
  calcOpen?: boolean;
  setCalcOpen?: (open: boolean) => void;
}

const DealerCard2: React.FC<DealerCardProps> = ({ vehicle }) => {
  const { vehicleDealerInfo, vehicleRegisterInfo } = vehicle;
  const navigate = useNavigate();
  const salesPersons = vehicle.salesPersons.filter(Boolean) as SalesPerson[];

  const coeLeft =
    (vehicleRegisterInfo.coeRemainingYears ?? 0) * 12 +
    (vehicleRegisterInfo.coeRemainingMonths ?? 0);

  const handleGmailOpen = (email: string) => {
    const vehicleLink = `https://staging.workhorse.com.sg/car-details/${vehicle?.vehicleId}`;
    const subject = encodeURIComponent('Enquiry from WorkHorse website');
    const body = encodeURIComponent(
      `Hi, I want to contact you about a vehicle listed on the WorkHorse website.\nHere is the vehicle link: ${vehicleLink}`
    );
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');
  };

  const handleWhatsAppOpen = (contactNo: string) => {
    const vehicleLink = `https://staging.workhorse.com.sg/car-details/${vehicle?.vehicleId}`;
    const message = encodeURIComponent(
      `Hi, I want to contact you about a vehicle listed on the WorkHorse website.\nHere is the vehicle link: ${vehicleLink}`
    );

    let cleaned = contactNo.replace(/\D/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = '60' + cleaned.slice(1);
    }
    const whatsappUrl = `https://wa.me/${cleaned}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCall = (phone: string) => {
    window.open(`tel:${phone}`);
  };

  return (
    <Box
      sx={{
        mt: 4,
        p: 2.5,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        backgroundColor: '#fff',
        boxShadow: 2,
        mx: 'auto',
      }}
    >
      {/* Top: Dealer Info */}
      {vehicleDealerInfo.dealerTypeId !== 2 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {/* Dealer Image */}
          <Box
            component="img"
            src={vehicleDealerInfo.imageUrl}
            alt={vehicleDealerInfo.dealerName}
            loading="lazy"
            sx={{
              width: 100,
              height: 100,
              borderRadius: 2,
              objectFit: 'contain',
              mb: 0.5,
            }}
          />

          {/* Dealer Details */}
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ cursor: 'pointer', color: 'text.primary' }}
              component={RouterLink}
              to={`/dealer-vehicles/${vehicleDealerInfo?.dealerCompanyId}`}
            >
              {vehicleDealerInfo.dealerCompanyName}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 200 }}
              >
                {vehicleDealerInfo.address || 'No address provided'}
              </Typography>
              <NavigateMenuButton address={vehicleDealerInfo.address} />
            </Box>
          </Box>
        </Box>
      ) : (
        // Only display type name for dealerTypeId 2
        <Typography
          variant="h6" // slightly bigger than subtitle1
          fontWeight={700} // bold
          color="text.primary" // black
          mb={2}
        >
          {vehicleDealerInfo.dealerTypeName}
        </Typography>

      )}



      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Box
            sx={{
              maxHeight: 170,
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: '4px',
              },
            }}
          >
            {salesPersons.map((person, index) => (
              <Box
                key={index}
                sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}
              >
                <Typography fontWeight={600} fontSize="0.9rem">
                  {person.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 1,
                    pl: 1,
                  }}
                >
                  {/* Phone */}
                  {person.contactNo && (
                    <Tooltip title={`Call ${person.contactNo}`} arrow>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                        onClick={() => handleCall(person.contactNo)}
                      >
                        <IconButton size="small" sx={{ color: '#1976d2' }}>
                          <PhoneIcon fontSize="small" />
                        </IconButton>
                        <Typography fontSize="0.75rem" sx={{ color: '#1976d2' }} >{person.contactNo}</Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {/* WhatsApp */}
                  {person.contactNo && (
                    <Tooltip title="WhatsApp" arrow>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          color: '#25D366',
                        }}
                        onClick={() => handleWhatsAppOpen(person.contactNo)}
                      >
                        <IconButton size="small" sx={{ color: '#25D366' }}>
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                        <Typography fontSize="0.75rem" color="#25D366">
                          {person.contactNo}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {/* Email */}
                  {person.email && (
                    <Tooltip title={`Email ${person.email}`} arrow>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleGmailOpen(person.email)}
                      >
                        <IconButton size="small">
                          <EmailIcon fontSize="small" />
                        </IconButton>
                        <Typography fontSize="0.75rem">{person.email}</Typography>
                      </Box>
                    </Tooltip>
                  )}

                </Box>
              </Box>
            ))}

          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DealerCard2;




